import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBuyerPitchSessions } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import NavHeader from '../components/NavHeader';
import { Calendar, Users, ArrowRight, Briefcase } from 'lucide-react';

const BuyerPitches = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const data = await getBuyerPitchSessions();
      setSessions(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavHeader />
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center text-slate-400">Loading sessions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <NavHeader />
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 mb-4">
              Buyer Pitch Podcast
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Pitch Directly to Decision Makers
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Get in front of real buyers with budget influence. Receive actionable feedback 
              and a shareable LinkedIn clip from your pitch.
            </p>
          </div>
        </div>
      </section>

      {/* Sessions Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        {error && (
          <div className="text-center text-red-400 mb-8">{error}</div>
        )}
        
        {sessions.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400 text-lg">No upcoming sessions scheduled.</p>
            <p className="text-slate-500 mt-2">Check back soon for new opportunities!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <Card 
                key={session._id}
                className="bg-slate-900/50 border-slate-800 hover:border-blue-500/50 transition-all group"
                data-testid={`session-card-${session._id}`}
              >
                <CardContent className="p-6">
                  {/* Buyer Info */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                      {session.buyer_name}
                    </h3>
                    {session.role && (
                      <p className="text-blue-400 font-medium">{session.role}</p>
                    )}
                    <p className="text-slate-400">{session.company}</p>
                  </div>

                  {/* Date & Spots */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    {session.recording_date && (
                      <Badge variant="outline" className="border-slate-700 text-slate-300">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(session.recording_date)}
                      </Badge>
                    )}
                    <Badge variant="outline" className="border-slate-700 text-slate-300">
                      <Users className="w-3 h-3 mr-1" />
                      {session.spots_filled}/{session.max_spots} spots
                    </Badge>
                  </div>

                  {/* Pain Points Preview */}
                  {session.pain_points && session.pain_points.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Focus Areas</p>
                      <p className="text-slate-400 text-sm line-clamp-2">
                        {session.pain_points[0]}
                      </p>
                    </div>
                  )}

                  {/* CTA */}
                  <Link to={`/buyer-pitches/${session.slug}`}>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid={`view-apply-btn-${session._id}`}
                    >
                      View & Apply
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Why Apply Section */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-slate-900/30 border border-slate-800 rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Why Founders Apply
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Real Buyer Feedback', desc: 'Understand what decision makers actually care about' },
              { title: 'Pressure-Test Messaging', desc: 'Refine your pitch with honest, direct feedback' },
              { title: 'Potential Customer', desc: 'Turn a pitch into a demo, intro, or advisory' },
              { title: 'Content Asset', desc: 'Get a shareable LinkedIn clip from your session' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <Briefcase className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm">&copy; {new Date().getFullYear()} The Founder Initiative. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BuyerPitches;
